/**
 * «Borrar este contacto» (derecho de supresión, Ley 25.326 art. 16; SPEC-BD B-12, §6.3).
 *
 * La casilla de no contactar arranca MARCADA: quien pide que borren sus datos casi siempre también pide
 * que no le escriban más, y olvidarse de marcarla es volver a escribirle con la próxima carga del mismo
 * Excel. Si ya está en no contactar, la casilla queda marcada y deshabilitada: la baja existente no se
 * toca (el backend no la pisa).
 */

import { useEffect, useId, useState } from "react";
import { AlertTriangle, Loader2, X } from "lucide-react";
import { useFocusTrap } from "../../../../hooks/useFocusTrap";
import type { EntradaSupresion } from "../../types.base";

const MAX_MOTIVO = 500;

interface Props {
  yaEnNoContactar: boolean;
  busy: boolean;
  error: string;
  onConfirmar: (s: EntradaSupresion) => void;
  onCancelar: () => void;
}

export function ModalSuprimir({ yaEnNoContactar, busy, error, onConfirmar, onCancelar }: Props) {
  const trapRef = useFocusTrap<HTMLDivElement>(true);
  const [dejar, setDejar] = useState(true);
  const [motivo, setMotivo] = useState("");
  const idTitulo = useId();
  const idCasilla = useId();
  const idMotivo = useId();
  const limpio = motivo.trim();
  const valido = limpio.length >= 1 && limpio.length <= MAX_MOTIVO;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape" && !busy) onCancelar(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [busy, onCancelar]);

  const enviar = () => {
    if (!valido || busy) return;
    onConfirmar({ motivo: limpio, dejarEnNoContactar: yaEnNoContactar ? true : dejar });
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 animate-fade-in p-4">
      <div ref={trapRef} role="dialog" aria-modal="true" aria-labelledby={idTitulo} className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 id={idTitulo} className="font-semibold text-lg text-slate-900">Borrar este contacto</h3>
          <button onClick={onCancelar} disabled={busy} aria-label="Cerrar"
            className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 disabled:opacity-50">
            <X size={18} />
          </button>
        </div>

        <div className="p-6 space-y-4 overflow-y-auto">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 bg-red-100">
              <AlertTriangle size={22} className="text-red-600" />
            </div>
            <div className="text-slate-600 text-sm leading-relaxed pt-1 space-y-2">
              <p>
                Se borran sus datos de la base, su conversación en Conversaciones y los archivos que mandó (también del proveedor de WhatsApp). Los mensajes que le mandamos quedan en las estadísticas, pero sin su número ni el texto.
                Si tiene mensajes en cola, se cancelan. No se puede deshacer.
              </p>
              <p className="text-xs text-slate-500">Si esta persona también está en una campaña por QR (por archivo), esos datos no se borran desde acá.</p>
            </div>
          </div>

          <div>
            <label htmlFor={idCasilla} className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
              <input id={idCasilla} type="checkbox" checked={yaEnNoContactar || dejar} disabled={busy || yaEnNoContactar}
                onChange={(e) => setDejar(e.target.checked)} className="mt-0.5 rounded border-slate-300 text-red-600 focus:ring-red-500" />
              Pidió no recibir más mensajes: dejar su número en la lista de no contactar
            </label>
            {yaEnNoContactar && <p className="text-xs text-slate-500 mt-1 ml-6">Ya está en no contactar: queda.</p>}
          </div>

          <div>
            <label htmlFor={idMotivo} className="text-xs font-semibold text-slate-600">Motivo (queda registrado)</label>
            <textarea id={idMotivo} value={motivo} rows={3} maxLength={MAX_MOTIVO} disabled={busy}
              placeholder="Por ejemplo: lo pidió por mail el 23/09"
              onChange={(e) => setMotivo(e.target.value.slice(0, MAX_MOTIVO))}
              className="w-full mt-1.5 rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 disabled:bg-slate-50" />
            <p className="text-[11px] text-slate-400 mt-1 text-right">{motivo.length} / {MAX_MOTIVO}</p>
          </div>

          {error && <p className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">{error}</p>}
        </div>

        <div className="flex gap-3 px-6 py-4 bg-slate-50 border-t border-slate-100">
          <button onClick={onCancelar} disabled={busy}
            className="flex-1 py-2.5 rounded-xl text-sm font-semibold border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50">
            Volver
          </button>
          <button onClick={enviar} disabled={!valido || busy}
            className="flex-1 py-2.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed">
            {busy && <Loader2 size={14} className="animate-spin" />}
            Borrar el contacto
          </button>
        </div>
      </div>
    </div>
  );
}

export default ModalSuprimir;
